// The name of the Azure Storage queue a simulation run reports through
// (`report_channel_name`): `<microgrid name>-<6 random digits>`.
//
// Azure queue names must be 3-63 characters of lowercase letters, digits and
// "-", start and end with a letter or digit, and never contain "--". A
// microgrid's container name can itself be 63 characters, so appending
// "-123456" can overrun the limit, and Azure then refuses the queue at
// createQueue ("OutOfRangeInput"). The name is only a label for the run: the
// sim takes it as an opaque channel name and gets the microgrid separately as
// `microgrid_name`, so it is free to differ from the microgrid's name.

export const QUEUE_NAME_MAX_LENGTH = 63;

// Used only when nothing usable is left of the microgrid name.
const FALLBACK_BASE = "run";

/**
 * Builds the run's queue name from the microgrid (container) name and a
 * six-digit number.
 *
 * A name whose queue name fits (56 characters or fewer, for a valid container
 * name) is used as is: `${simulationName}-${sixDigits}`. A longer name keeps
 * its END, the part the user typed after the customer prefix, cut so the
 * whole queue name is at most 63 characters. Whatever the input, the result
 * satisfies Azure's queue-name rules: it is lowercased, any character other
 * than a-z, 0-9 and "-" becomes "-", runs of "-" collapse to one, and the
 * kept part never starts or ends with "-".
 *
 * @param {string} simulationName the microgrid's container name
 * @param {number|string} sixDigits exactly six digits, e.g. 482913
 * @returns {string} a valid Azure queue name of at most 63 characters
 */
export const runQueueName = (simulationName, sixDigits) => {
  const suffix = String(sixDigits);
  if (!/^\d{6}$/.test(suffix)) {
    throw new RangeError(`runQueueName: expected six digits, got "${suffix}".`);
  }

  // Room for the kept part of the name, after "-" and the digits: 56.
  const maxBaseLength = QUEUE_NAME_MAX_LENGTH - 1 - suffix.length;

  const cleaned = String(simulationName ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-");

  const base = cleaned.slice(-maxBaseLength).replace(/^-+|-+$/g, "");

  return `${base || FALLBACK_BASE}-${suffix}`;
};
